import { areIntervalsOverlapping, format } from "date-fns";
import { GameView, Schedule } from "types";

export const TIME_FORMAT = {
  DEFAULT: "yyyy-MM-dd HH:mm:ss",
  DATE: "yyyy-MM-dd",
  HOURS_MIN: "HH:mm",
  BASE_HOURS_TIME: "h:mm a",
};

export const getGameFromScheduleById = (currentDay: Date, schedule: Schedule, id: number) => {
    return schedule.days.find((day) => 
      new Date(day.date).getTime() === currentDay.getTime()
    )?.games.find((game) => game.id === id);
}

export const formatTime = (
    date: string | number | Date,
    formatType: string = TIME_FORMAT.HOURS_MIN
  ) => format(new Date(date), formatType).replace(/\s/g, "");

export const findGameById = (games: GameView[], gameId: number) => games.find(g => g.id === gameId);

export const isOverlapping = (a: GameView, b: GameView) => a.courtId === b.courtId && areIntervalsOverlapping({ start: a.startTime, end: a.endTime }, { start: b.startTime, end: b.endTime});

/**
 * Checks if two games have a time overlap (regardless of court).
 */
const hasTimeOverlap = (a: GameView, b: GameView): boolean =>
  areIntervalsOverlapping(
    { start: new Date(a.startTime), end: new Date(a.endTime) },
    { start: new Date(b.startTime), end: new Date(b.endTime) }
  );

/**
 * Checks if two games have a scheduling conflict:
 * 1. Same court at overlapping times, OR
 * 2. A team in one game is also playing in the other game at an overlapping time
 */
export const hasSchedulingConflict = (a: GameView, b: GameView): boolean => {
  if (a.id === b.id) return false;
  if (!hasTimeOverlap(a, b)) return false;

  // Court conflict: same court, overlapping times
  if (a.courtId === b.courtId) return true;

  // Team conflict: a team appears in both games at overlapping times
  const teamsA = [a.homeTeamId, a.awayTeamId];
  const teamsB = [b.homeTeamId, b.awayTeamId];
  return teamsA.some((teamId) => teamsB.includes(teamId));
};

/**
 * Given a list of games, returns the set of game IDs that have at least one scheduling conflict.
 */
export const getConflictingGameIds = (games: GameView[]): Set<number> => {
  const conflicting = new Set<number>();
  for (let i = 0; i < games.length; i++) {
    for (let j = i + 1; j < games.length; j++) {
      if (hasSchedulingConflict(games[i], games[j])) {
        conflicting.add(games[i].id);
        conflicting.add(games[j].id);
      }
    }
  }
  return conflicting;
};